import { reportQueries, useReportStore } from '@/entities/report';
import {
    reportMainTabStateTypeGuard,
    reportSubTabStateTypeGuard,
} from '@/entities/report/model/report.types';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { ReportTemplate, TabMenu } from '@/shared/ui';
import { AiReview } from '@/widgets/report/ai-review';
import {
    ReportStntsContentsViewer,
    ReportStntsThumbList,
    ReportSummary,
} from '@/widgets/report/detail/table-body/stnts';
import { ReportHeaderLeft, ReportHeaderStntsRight } from '@/widgets/report/detail/table-header';
import { ReportDetailHeader } from '@/widgets/section-header';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

export const Route = createFileRoute('/report/stnts/')({
    component: RouteComponent,
});

function RouteComponent() {
    const queryClient = useQueryClient();
    const { showModal } = useModalStore();
    const { stntReport, setStntReport } = useReportStore();

    // 점수보기 toggle
    const [scoreViewToggle, setScoreViewToggle] = useState(false);
    const scoreViewHandler = () => {
        setScoreViewToggle((prev) => !prev);
    };

    // 리포트 api 호출
    const params = {
        evlId: stntReport?.targetId!,
    };
    const { data, isLoading } = reportQueries.stnts.getReportEvalDetailList(
        params,
        !!stntReport?.targetId
    );

    useEffect(() => {
        if (data) {
            setStntReport({
                stntId: data.stntEvlScrInfoList[stntReport?.stntsListIdx ?? 0]?.userId,
            });
        }
    }, [stntReport.stntsListIdx, data]);

    // 메인 탭 변경
    const mainTabChange = useCallback(
        (val: string) => {
            if (val === 'AI 총평' && stntReport.stntsListIdx !== 0 && !stntReport.stntsListIdx) {
                setStntReport({ stntsListIdx: 0, subTab: 'student' });
            }
            if (val === '상세 보기' && !!stntReport.stntsListIdx) {
                setStntReport({ subTab: 'student', stntsListIdx: stntReport.stntsListIdx });
            }
            if (reportMainTabStateTypeGuard(val)) {
                setStntReport({ mainTab: val });
            }
        },
        [setStntReport, showModal]
    );

    // 서브 탭 변경
    const subTabChange = useCallback(
        (val: string) => {
            if (reportSubTabStateTypeGuard(val)) {
                // 콘텐츠 정보 클릭 시 학생 index 초기화
                if (val === 'contents') {
                    setStntReport({ stntsListIdx: undefined });
                    if (stntReport.thumbListIdx === undefined) {
                        setStntReport({ thumbListIdx: 0 });
                    }
                }
                setStntReport({ subTab: val });
            }
        },
        [setStntReport, stntReport.thumbListIdx]
    );

    // 썸네일 리스트 index 변경
    const tumbListChange = useCallback(
        (idx: number) => {
            setStntReport({ thumbListIdx: idx });
        },
        [setStntReport]
    );

    // 학생 index 변경
    const studentChange = useCallback(
        (idx: number) => {
            setStntReport({ stntsListIdx: idx });
        },
        [setStntReport]
    );

    if (isLoading) return null;
    if (!data) return null;

    // 리포트 제출 카운트
    const submAtCnt = `${data?.submStntCnt} / ${data?.notSubmStntCnt! + data?.submStntCnt!}`;

    const selectedModule =
        stntReport.thumbListIdx === 0 || stntReport.thumbListIdx
            ? data?.mdulEvlInfoList[stntReport.thumbListIdx ?? 0]
            : undefined;

    const submitList = data?.stntEvlScrInfoList.filter(
        (item) => !(item.submAt === 'N' && stntReport?.usage === '2')
    );

    const selectedStntInfo =
        stntReport.stntsListIdx === 0 || stntReport.stntsListIdx
            ? submitList[stntReport.stntsListIdx ?? 0]
            : undefined;

    const contentsNum =
        stntReport.thumbListIdx === 0 || stntReport.thumbListIdx ? stntReport.thumbListIdx + 1 : 1;

    return (
        <div className="contwrap">
            <div className="contInner report-content">
                <ReportDetailHeader setType={stntReport.usage} title={data?.evlNm} />
                <ReportSummary
                    evlId={stntReport?.targetId!}
                    scoreViewToggle={scoreViewToggle}
                    onScoreViewHandler={scoreViewHandler}
                    studentChange={studentChange}
                    tumbListChange={tumbListChange}
                    subTabChange={subTabChange}
                    stntEvlScrInfoList={data?.stntEvlScrInfoList}
                />
                <TabMenu
                    className="tab-type8 tab-tit2"
                    tabItems={[
                        { tabName: '상세 보기', value: '상세 보기' },
                        { tabName: 'AI 총평', value: 'AI 총평' }, // ai 총평 임시 비활성화
                    ]}
                    selected={stntReport.mainTab}
                    onChange={mainTabChange}
                />
                <div className="con-mt3">
                    <ReportTemplate
                        typeAnswerTable={true}
                        // 테이블 해더: 항목, 점수(평가기준)
                        headerLeft={<ReportHeaderLeft toolTipIcon={stntReport?.usage === '2'} />}
                        // 테이블 해더: 콘텐츠 정보, 학생 이름
                        headerRight={
                            <ReportHeaderStntsRight
                                stntEvlScrInfoList={submitList ?? []}
                                selectedtab={stntReport.subTab}
                                selectedStntsIdx={stntReport.stntsListIdx}
                                subTabChange={subTabChange}
                                studentChange={studentChange}
                            />
                        }
                        // 테이블 바디: 썸네일 리스트
                        bodyLeft={
                            stntReport.mainTab !== 'AI 총평' && (
                                <ReportStntsThumbList
                                    thumbList={data?.mdulEvlInfoList ?? []}
                                    selectedSubTab={stntReport.subTab}
                                    selectedStntsIndex={stntReport.stntsListIdx}
                                    selectedThumbIndex={stntReport.thumbListIdx}
                                    onThumbClick={tumbListChange}
                                />
                            )
                        }
                        // 테이블 바디: 콘텐츠, 뷰어
                        bodyRight={
                            stntReport.mainTab !== 'AI 총평' && (
                                <ReportStntsContentsViewer
                                    selectedStntsThumbContent={selectedModule}
                                    submAtCnt={submAtCnt}
                                    selectedStntsInfo={selectedStntInfo}
                                    contentsNum={contentsNum}
                                    selectedSubTab={stntReport.subTab}
                                />
                            )
                        }
                        bodyTotal={
                            stntReport.mainTab === 'AI 총평' && (
                                <AiReview
                                    evalId={stntReport?.targetId!}
                                    stntId={stntReport?.stntId ?? ''}
                                />
                            )
                        }
                    />
                </div>
            </div>
        </div>
    );
}
